/**
 * BM-H8 — Mobile "Failed to fetch" self-diagnostic.
 *
 * A user hitting bare `TypeError: Failed to fetch` on their phone (usually
 * mobile Safari) can navigate to /diagnostic and see:
 *
 *   1. Whether /api/system/ping is reachable at all (raw server up/down)
 *   2. What the server sees as their Origin header
 *   3. Whether that Origin is in the server's CORS allow-list
 *   4. Whether their session cookie is being sent
 *   5. What the current server time and deploy trigger are
 *
 * No axios interceptor here — we call plain fetch so we can see the raw
 * network layer. Runs unauthenticated. Never crashes; every failure mode
 * shows a red row with the actual error string.
 */
import React, { useEffect, useState, useCallback } from "react";
import { API } from "../config/api";

const CHECK_STATES = {
  pending: { label: "Checking…", color: "#888" },
  ok: { label: "OK", color: "#15803d" },
  fail: { label: "FAIL", color: "#b91c1c" },
  warn: { label: "WARN", color: "#a16207" },
};

function StatusPill({ state }) {
  const s = CHECK_STATES[state] || CHECK_STATES.pending;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        color: "#fff",
        background: s.color,
        minWidth: 60,
        textAlign: "center",
      }}
    >
      {s.label}
    </span>
  );
}

function Row({ label, state, detail }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "12px 0",
        borderBottom: "1px solid #eee",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 200px", minWidth: 0 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
        {detail ? (
          <div
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 12,
              color: "#555",
              wordBreak: "break-all",
              whiteSpace: "pre-wrap",
            }}
          >
            {detail}
          </div>
        ) : null}
      </div>
      <StatusPill state={state} />
    </div>
  );
}

export default function MobileDiagnostic() {
  const [running, setRunning] = useState(false);
  const [pingState, setPingState] = useState("pending");
  const [pingResp, setPingResp] = useState(null);
  const [pingError, setPingError] = useState("");
  const [runAt, setRunAt] = useState(null);

  const runChecks = useCallback(async () => {
    setRunning(true);
    setPingState("pending");
    setPingResp(null);
    setPingError("");
    setRunAt(new Date().toISOString());
    try {
      const t0 = performance.now();
      const resp = await fetch(`${API}/system/ping`, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      const ms = Math.round(performance.now() - t0);
      if (!resp.ok) {
        setPingState("fail");
        setPingError(`HTTP ${resp.status} in ${ms}ms`);
      } else {
        const body = await resp.json();
        body._roundtrip_ms = ms;
        setPingResp(body);
        setPingState("ok");
      }
    } catch (err) {
      setPingState("fail");
      setPingError(`${err.name}: ${err.message}`);
    } finally {
      setRunning(false);
    }
  }, []);

  useEffect(() => {
    runChecks();
  }, [runChecks]);

  // Derived checks that only exist if ping succeeded.
  const originEcho = pingResp?.request?.origin || "";
  const cookiePresent = Boolean(pingResp?.request?.cookie_present);
  const allowedOrigins = pingResp?.cors?.allow_origins || [];
  const originInAllowList =
    originEcho && allowedOrigins.length > 0 && allowedOrigins.includes(originEcho);

  const originState = !pingResp
    ? "pending"
    : originInAllowList
    ? "ok"
    : "fail";
  const cookieState = !pingResp ? "pending" : cookiePresent ? "ok" : "warn";
  const clientOrigin =
    typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div
      style={{
        maxWidth: 640,
        margin: "0 auto",
        padding: "24px 16px",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: "#111",
      }}
    >
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>Connection diagnostic</h1>
      <p style={{ color: "#666", marginTop: 0, marginBottom: 16, fontSize: 14 }}>
        If Bonsai Mail is showing "Failed to fetch", this page tells us why.
        Screenshot the whole page and send it in.
      </p>

      <div style={{ marginBottom: 16 }}>
        <button
          onClick={runChecks}
          disabled={running}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "1px solid #01696F",
            background: running ? "#e5e5e5" : "#01696F",
            color: running ? "#666" : "#fff",
            fontWeight: 600,
            cursor: running ? "wait" : "pointer",
            fontSize: 14,
          }}
        >
          {running ? "Running…" : "Re-run checks"}
        </button>
        {runAt ? (
          <span style={{ marginLeft: 12, color: "#666", fontSize: 12 }}>
            Last run: {runAt}
          </span>
        ) : null}
      </div>

      <div style={{ background: "#fafafa", padding: 16, borderRadius: 8 }}>
        <Row
          label="Server reachable"
          state={pingState}
          detail={
            pingState === "ok"
              ? `Roundtrip ${pingResp?._roundtrip_ms}ms · deploy ${
                  pingResp?.deploy_trigger || "unknown"
                }`
              : pingError || `${API}/system/ping`
          }
        />
        <Row
          label="Origin allowed"
          state={originState}
          detail={
            pingResp
              ? `Your browser sent Origin: ${originEcho || "(none)"}\nServer accepts ${allowedOrigins.length} origins${
                  originEcho && !originInAllowList
                    ? "\n⚠ Your Origin is NOT in the allow list — this is the cause of 'Failed to fetch'"
                    : ""
                }`
              : "Waiting for server response"
          }
        />
        <Row
          label="Session cookie"
          state={cookieState}
          detail={
            pingResp
              ? cookiePresent
                ? "Cookie header is being sent"
                : "No cookie header sent — you may need to log in again"
              : "Waiting for server response"
          }
        />
        <Row
          label="Client environment"
          state="ok"
          detail={`Origin: ${clientOrigin}\nAPI base: ${API}\nUA: ${
            typeof navigator !== "undefined" ? navigator.userAgent : "(unknown)"
          }`}
        />
        {pingResp?.server_time_utc ? (
          <Row
            label="Server time"
            state="ok"
            detail={`${pingResp.server_time_utc} · Python ${pingResp.python}`}
          />
        ) : null}
      </div>

      {pingResp ? (
        <details style={{ marginTop: 24 }}>
          <summary style={{ cursor: "pointer", color: "#01696F", fontWeight: 600 }}>
            Raw ping response
          </summary>
          <pre
            style={{
              background: "#111",
              color: "#0f0",
              padding: 12,
              borderRadius: 8,
              overflowX: "auto",
              fontSize: 11,
              marginTop: 8,
            }}
          >
            {JSON.stringify(pingResp, null, 2)}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
